import axios from 'axios'
import EventEmitter from 'events'

const emptyCache = { features: [] }

class FF4J extends EventEmitter {
    config
    context
    cache
    loopIdentificationTimeout

    constructor(config, context) {
        super()

        this.config = config
        this.context = context
        this.cache = emptyCache

        if (config.useLocalStorage) {
            this.cache = this.getCacheFromLocalStorage() || emptyCache
        }
    }

    fetchLoop() {
        this.loopIdentificationTimeout = setTimeout(async () => {
            try {
                await this.fetchFeatures()
                this.emit('update')
            } catch (err) {
                console.error(
                    "Background update feature flags can't be finished",
                    err
                )
            } finally {
                if (this.loopIdentificationTimeout) {
                    this.fetchLoop()
                }
            }
        }, this.config.revalidateInterval)
    }

    updateContext(context) {
        this.context = context
        this.emit('update')
    }

    async fetchFeatures() {
        const { data: features } = await axios.get(
            `${this.config.apiUrl}/store/features`,
            {
                timeout: this.config.fetchTimeout
            }
        )

        if (Array.isArray(features)) {
            this.cache.features = features

            if (this.config.useLocalStorage) {
                this.updateCacheAtLocalStorage()
            }
        }
    }

    resolveFeatures(flags) {
        const data = {}
        flags.forEach((flagKey) => {
            data[flagKey] = this.resolveFeature(flagKey, false)
        })
        return data
    }

    resolveFeature(flagKey, defaultValue) {
        const feature = this.getFeature(flagKey)
        if (!feature) {
            return defaultValue
        }

        if (this.canUseFeature(feature)) {
            return feature.enable
        }

        return false
    }

    clean() {
        if (this.loopIdentificationTimeout) {
            clearTimeout(this.loopIdentificationTimeout)
            this.loopIdentificationTimeout = undefined
        }
    }

    getFeature(flagKey) {
        return this.cache.features.find((feat) => feat.uid === flagKey)
    }

    canUseFeature(feature) {
        if (feature.permissions.length === 0) {
            return true
        }

        const myPermissions = this.context?.permissions ?? []

        return feature.permissions.some((permission) =>
            myPermissions.includes(permission)
        )
    }

    getCacheFromLocalStorage() {
        const rawItem = localStorage.getItem(this.config.localStorageKey)
        if (!rawItem) {
            return null
        }

        return JSON.parse(rawItem)
    }

    updateCacheAtLocalStorage() {
        localStorage.setItem(
            this.config.localStorageKey,
            JSON.stringify(this.cache)
        )
    }
}

export default FF4J
